import type { InstanceId, ProductId, RoomId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { DomainEvent } from '@/core/events/types';
import type { LedgerEntry } from '@/core/persistence/db';
import type { GameSave, SlotContent } from '@/core/persistence/schema/v1';
import { grantRewards } from '@/core/domain/rewards';
import { nextRotation, slotIndex, slotKey, slotParent, type SlotKey } from './geometry';
import { canPlace, isRightSpot, lampInside, zonesForTarget, type PlacementTarget } from './rules';

export interface HandlerEnv {
  content: ContentRegistry;
  nowWallMs: number;
  origin: 'player' | 'system' | 'undo' | 'redo';
  /** Ledger rows produced by this command; written in the same transaction as the next save. */
  ledger?: LedgerEntry[];
}

/** Where an instance currently lives in a room. */
type Location =
  { kind: 'placement'; roomId: RoomId } | { kind: 'slot'; roomId: RoomId; key: SlotKey } | { kind: 'bag' };

function locate(draft: GameSave, instanceId: InstanceId): Location {
  const inst = draft.inventory.instances[instanceId];
  if (inst?.location.kind !== 'placed') return { kind: 'bag' };
  const room = draft.rooms[inst.location.roomId];
  if (instanceId in room.placements) return { kind: 'placement', roomId: room.roomId };
  for (const [key, c] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
    if (c.kind === 'instance' && c.instanceId === instanceId)
      return { kind: 'slot', roomId: room.roomId, key };
  }
  return { kind: 'bag' };
}

function childSlots(draft: GameSave, roomId: RoomId, parent: InstanceId): [SlotKey, SlotContent][] {
  return (Object.entries(draft.rooms[roomId].slots) as [SlotKey, SlotContent][]).filter(
    ([key]) => slotParent(key) === parent,
  );
}

/** Detach an instance from wherever it is (children stay attached to it). */
function detach(draft: GameSave, instanceId: InstanceId): Location {
  const loc = locate(draft, instanceId);
  if (loc.kind === 'placement') {
    const room = draft.rooms[loc.roomId];
    room.placements = Object.fromEntries(Object.entries(room.placements).filter(([id]) => id !== instanceId));
  } else if (loc.kind === 'slot') {
    const room = draft.rooms[loc.roomId];
    room.slots = Object.fromEntries(Object.entries(room.slots).filter(([key]) => key !== loc.key));
  }
  return loc;
}

const placeFailure = {
  SLOT_OCCUPIED: 'SLOT_OCCUPIED',
  NOT_ACCEPTED: 'NOT_ACCEPTED',
  NOT_FOUND: 'NOT_FOUND',
} as const;

/** placeItem: from the bag or move within/between rooms (content-schemas §13.1, §14.13). */
export function placeItem(draft: GameSave, cmd: CommandOf<'placeItem'>, env: HandlerEnv): CommandResult {
  const { content } = env;
  const inst = draft.inventory.instances[cmd.instanceId];
  if (!inst) return { ok: false, error: 'NOT_FOUND' };
  const def = content.items.get(inst.itemId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  const check = canPlace(content, draft, cmd.instanceId, cmd.target);
  if (!check.ok) {
    const error =
      check.reason in placeFailure
        ? placeFailure[check.reason as keyof typeof placeFailure]
        : 'INVALID_PLACEMENT';
    return { ok: false, error, detail: { reason: check.reason } };
  }

  const fromBag = inst.location.kind === 'bag';
  const previous = detach(draft, cmd.instanceId);
  const target: PlacementTarget = cmd.target;
  const room = draft.rooms[target.roomId];

  // Children of a parent moving to another room travel with it.
  if (previous.kind === 'placement' && previous.roomId !== target.roomId) {
    for (const [key, c] of childSlots(draft, previous.roomId, cmd.instanceId)) {
      const from = draft.rooms[previous.roomId];
      from.slots = Object.fromEntries(Object.entries(from.slots).filter(([k]) => k !== key));
      room.slots[key] = c;
      if (c.kind === 'instance') {
        const child = draft.inventory.instances[c.instanceId];
        if (child) child.location = { kind: 'placed', roomId: target.roomId };
      }
    }
  }

  let rotation: 0 | 90 | 180 | 270 = 0;
  if (target.layer === 'surface') {
    room.slots[slotKey(target.parentInstanceId, target.slotIndex)] = {
      kind: 'instance',
      instanceId: cmd.instanceId,
    };
  } else if (target.layer === 'wall') {
    room.placements[cmd.instanceId] = {
      instanceId: cmd.instanceId,
      layer: 'wall',
      wall: target.wall,
      offset: target.offset,
    };
  } else {
    rotation = target.rotation;
    room.placements[cmd.instanceId] = {
      instanceId: cmd.instanceId,
      layer: target.layer,
      x: target.x,
      y: target.y,
      rotation: target.rotation,
    };
  }
  inst.location = { kind: 'placed', roomId: target.roomId };

  const event: DomainEvent = {
    type: 'ITEM_PLACED',
    roomId: target.roomId,
    instanceId: cmd.instanceId,
    itemId: def.id,
    kind: def.kind,
    layer: def.layer,
    fromBag,
    rotation,
    zones: zonesForTarget(content, draft, def, target),
    rightSpot: isRightSpot(content, draft, cmd.instanceId, target),
    acquiredBy: inst.acquiredBy,
    ...(target.layer === 'surface' ? { parentInstanceId: target.parentInstanceId } : {}),
    origin: env.origin,
  };
  return { ok: true, events: [event] };
}

/** rotateItem: next allowed rotation in place; refused (no change) if it would not fit. */
export function rotateItem(draft: GameSave, cmd: CommandOf<'rotateItem'>, env: HandlerEnv): CommandResult {
  const { content } = env;
  const inst = draft.inventory.instances[cmd.instanceId];
  if (inst?.location.kind !== 'placed') return { ok: false, error: 'NOT_FOUND' };
  const roomId = inst.location.roomId;
  const placement = draft.rooms[roomId].placements[cmd.instanceId];
  const def = content.items.get(inst.itemId);
  if (!placement || placement.layer === 'wall' || !def) return { ok: false, error: 'INVALID_ARGUMENT' };
  const rotation = nextRotation(def.rotations, placement.rotation, cmd.reverse ? -1 : 1);
  if (rotation === placement.rotation) return { ok: false, error: 'INVALID_ARGUMENT' };
  const target: PlacementTarget = {
    layer: placement.layer,
    roomId,
    x: placement.x,
    y: placement.y,
    rotation,
  };
  const check = canPlace(content, draft, cmd.instanceId, target);
  if (!check.ok) return { ok: false, error: 'INVALID_PLACEMENT', detail: { reason: check.reason } };
  placement.rotation = rotation;
  return {
    ok: true,
    events: [
      {
        type: 'ITEM_ROTATED',
        roomId,
        instanceId: cmd.instanceId,
        itemId: def.id,
        rotation,
        rightSpot: isRightSpot(content, draft, cmd.instanceId, target),
        origin: env.origin,
      },
    ],
  };
}

/** storeItem: back to the bag; anything on its surface comes along (decor → bag, products → stacks). */
export function storeItem(draft: GameSave, cmd: CommandOf<'storeItem'>, env: HandlerEnv): CommandResult {
  const inst = draft.inventory.instances[cmd.instanceId];
  if (inst?.location.kind !== 'placed') return { ok: false, error: 'NOT_FOUND' };
  const def = env.content.items.get(inst.itemId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (!def.removable) return { ok: false, error: 'NOT_REMOVABLE' };
  const roomId = inst.location.roomId;
  const cascaded = { instanceIds: [] as InstanceId[], products: [] as { itemId: ProductId; qty: number }[] };
  for (const [key, c] of childSlots(draft, roomId, cmd.instanceId)) {
    const room = draft.rooms[roomId];
    room.slots = Object.fromEntries(Object.entries(room.slots).filter(([k]) => k !== key));
    if (c.kind === 'instance') {
      const child = draft.inventory.instances[c.instanceId];
      if (child) child.location = { kind: 'bag' };
      cascaded.instanceIds.push(c.instanceId);
    } else {
      draft.inventory.productStacks[c.itemId] = (draft.inventory.productStacks[c.itemId] ?? 0) + 1;
      const line = cascaded.products.find((p) => p.itemId === c.itemId);
      if (line) line.qty += 1;
      else cascaded.products.push({ itemId: c.itemId, qty: 1 });
    }
  }
  detach(draft, cmd.instanceId);
  inst.location = { kind: 'bag' };
  return {
    ok: true,
    events: [
      {
        type: 'ITEM_STORED',
        roomId,
        instanceId: cmd.instanceId,
        itemId: def.id,
        cascaded,
        origin: env.origin,
      },
    ],
  };
}

/**
 * Boot repair: lamps placed inside a shelf before lamps had to sit on top move up to a free top slot of the
 * same piece, or back to the bag when the top is full. True when anything moved.
 */
export function liftLampsOnTop(draft: GameSave, content: ContentRegistry): boolean {
  let moved = false;
  for (const room of Object.values(draft.rooms)) {
    for (const [key, c] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
      if (c.kind !== 'instance') continue;
      const inst = draft.inventory.instances[c.instanceId];
      const def = inst && content.items.get(inst.itemId);
      const parent = room.placements[slotParent(key)];
      const parentInst = parent && draft.inventory.instances[parent.instanceId];
      const parentDef = parentInst && content.items.get(parentInst.itemId);
      if (!inst || !def || !parentDef?.slots || !lampInside(def, parentDef, slotIndex(key))) continue;
      const free = Array.from({ length: parentDef.slots.count }, (_, i) => i).find(
        (i) => !lampInside(def, parentDef, i) && !(slotKey(slotParent(key), i) in room.slots),
      );
      room.slots = Object.fromEntries(Object.entries(room.slots).filter(([k]) => k !== key));
      if (free === undefined) inst.location = { kind: 'bag' };
      else room.slots[slotKey(slotParent(key), free)] = c;
      moved = true;
    }
  }
  return moved;
}

/** openBox: contents go to the bag; the box disappears (content-schemas §3.7). */
export function openBox(draft: GameSave, cmd: CommandOf<'openBox'>, env: HandlerEnv): CommandResult {
  const box = draft.inventory.boxes[cmd.boxInstanceId];
  if (!box) return { ok: false, error: 'NOT_FOUND' };
  if (box.opened) return { ok: false, error: 'ALREADY_CLAIMED' };
  const def = env.content.boxes.get(box.boxDefId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (def.requiresMiniGame) {
    const state = draft.miniGames[def.requiresMiniGame.miniGameId];
    if (!state?.levelsCleared.includes(def.requiresMiniGame.level)) {
      return { ok: false, error: 'LOCKED', detail: { miniGameId: def.requiresMiniGame.miniGameId } };
    }
  }
  box.opened = true;
  const events = grantRewards(draft, env.content, `box:${cmd.boxInstanceId}`, def.contents, 'starter');
  return {
    ok: true,
    events: [
      { type: 'BOX_OPENED', boxInstanceId: cmd.boxInstanceId, boxDefId: def.id, rewards: def.contents },
      ...events,
    ],
  };
}
